// @ts-check
// Enviar teste (spec/whatsapp.md 18.5 com a revisão 2, K42): o teste vai só para o "WhatsApp para testes" do próprio
// membro (gravado aqui, uma troca a cada 24 h), com os dados de um card do público (lido com o papel de quem testa) ou
// com dados fictícios. Mostra a prévia, o aviso de que a Meta cobra o teste e quantos testes restam hoje (10 por dia).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { fetchAudienceCards } from '../../data/audiences.js';
import { getWhatsappTestPhone, sendWhatsappCampaignTest, setWhatsappTestPhone } from '../../data/whatsapp-campaigns.js';
import { Modal } from '../../ui/modal.js';
import { Button } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { formatDateTime } from '../../lib/format.js';
import { WhatsappMessagePreview } from './whatsapp-message-preview.js';
import { sampleValues } from '../../lib/whatsapp-campaigns.js';
import { describeCampaignError } from './whatsapp-campaign-review-modal.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaign} WhatsappCampaign
 * @typedef {import('../../data/whatsapp-campaigns.js').WhatsappTestPhone} WhatsappTestPhone
 */

/** Telefone com DDI e DDD, só dígitos. @param {string} value */
export const normalizeTestPhoneInput = (value) => String(value ?? '').replace(/\D/g, '');

/**
 * `values` são os da amostra do editor; depois do envio, a prévia mostra o texto que saiu.
 * @param {{ campaign: WhatsappCampaign, template: any, values?: Record<string, string>, onClose: () => void, beforeSend?: () => Promise<unknown> }} props
 */
export function WhatsappCampaignTestDialog({ campaign, template, values = {}, onClose, beforeSend }) {
  const [testPhone, setTestPhone] = useState(/** @type {WhatsappTestPhone | null} */ (null));
  const [phoneInput, setPhoneInput] = useState('');
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [cards, setCards] = useState(/** @type {Array<{ id: string, name: string }> | null} */ (null));
  const [cardId, setCardId] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sentText, setSentText] = useState(/** @type {string | null} */ (null));

  useEffect(() => {
    getWhatsappTestPhone()
      .then((value) => {
        setTestPhone(value);
        setPhoneInput(value.phone ?? '');
        setIsEditingPhone(!value.phone);
      })
      .catch((requestError) => setError(describeCampaignError(requestError)));
    if (campaign.audienceDefinition) {
      fetchAudienceCards({ definition: campaign.audienceDefinition, limit: 20 })
        .then((page) => setCards(page.cards.map((card) => ({ id: card.id, name: card.name }))))
        .catch(() => setCards([]));
    } else setCards([]);
  }, [campaign.id]);

  const savePhone = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const saved = await setWhatsappTestPhone(normalizeTestPhoneInput(phoneInput));
      setTestPhone(saved);
      setIsEditingPhone(false);
    } catch (requestError) {
      setError(describeCampaignError(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  const send = async () => {
    setIsSending(true);
    setError(null);
    try {
      if (beforeSend) await beforeSend();
      const result = await sendWhatsappCampaignTest(campaign.id, { cardId: cardId || null });
      setSentText(result.text);
      setTestPhone((current) => (current ? { ...current, testsUsed: result.testsUsed, testsRemaining: Math.max(0, result.testsLimit - result.testsUsed) } : current));
      showSuccessSnackbar(t('whatsappCampaigns.test.sent'));
    } catch (requestError) {
      setError(describeCampaignError(requestError));
    } finally {
      setIsSending(false);
    }
  };

  const canChangePhone = !testPhone?.canChangeAt;
  const remaining = testPhone?.testsRemaining ?? 0;
  return html`<${Modal} title=${t('whatsappCampaigns.test.title')} subtitle=${t('whatsappCampaigns.test.subtitle')}
    confirmLabel=${t('whatsappCampaigns.test.send')} isConfirmDisabled=${!testPhone?.phone || isEditingPhone || remaining <= 0} isLoading=${isSending}
    onClose=${onClose} onConfirm=${send}>
    <div class="wa-test-dialog">
      ${testPhone === null && !error
        ? html`<${Skeleton} height=${36} />`
        : html`<div class="form-field">
            <span class="form-field-label">${t('whatsappCampaigns.test.phone')}</span>
            ${isEditingPhone
              ? html`<div class="wa-test-phone-edit">
                  <input class="text-input" inputMode="tel" autoComplete="tel" value=${phoneInput} placeholder="5511999990000" aria-label=${t('whatsappCampaigns.test.phone')}
                    onInput=${(/** @type {Event} */ event) => setPhoneInput(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
                  <${Button} title=${t('whatsappCampaigns.test.savePhone')} variant="secondary" size="small" isLoading=${isSaving}
                    disabled=${normalizeTestPhoneInput(phoneInput).length < 10} onClick=${savePhone} />
                </div>
                <p class="wa-editor-help">${t('whatsappCampaigns.test.phoneHelp')}</p>`
              : html`<div class="wa-test-phone">
                  <strong data-testid="wa-test-phone">+${testPhone?.phone}</strong>
                  ${canChangePhone
                    ? html`<${Button} title=${t('whatsappCampaigns.test.changePhone')} variant="tertiary" size="small" onClick=${() => setIsEditingPhone(true)} />`
                    : html`<span class="wa-editor-help">${t('whatsappCampaigns.test.changeAvailableAt', { date: formatDateTime(testPhone?.canChangeAt) })}</span>`}
                </div>`}
          </div>`}
      <label class="form-field">
        <span class="form-field-label">${t('whatsappCampaigns.test.useDataOf')}</span>
        <select class="text-input integration-select" value=${cardId} disabled=${cards === null}
          onChange=${(/** @type {Event} */ event) => setCardId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
          <option value="">${t('whatsappCampaigns.test.sampleData')}</option>
          ${(cards ?? []).map((card) => html`<option key=${card.id} value=${card.id} selected=${card.id === cardId}>${card.name}</option>`)}
        </select>
      </label>
      ${template
        ? html`<${WhatsappMessagePreview} template=${template} title=${t('whatsappCampaigns.test.preview')}
            values=${sentText ? sampleValues(template, { text: sentText }).values : values} />`
        : null}
      ${sentText ? html`<p class="wa-notice" data-tone="success" role="status"><${Icon} name="IconCircleCheck" size=${16} /><span>${t('whatsappCampaigns.test.sentText')}</span></p>` : null}
      <p class="wa-notice" data-tone="info"><${Icon} name="IconCoin" size=${16} /><span>${t('whatsappCampaigns.test.costNote')}</span></p>
      ${testPhone ? html`<p class="wa-test-remaining" aria-live="polite">${t('whatsappCampaigns.test.remaining', { count: remaining })}</p>` : null}
      ${error ? html`<p class="wa-field-error" role="alert">${error}</p>` : null}
    </div>
  <//>`;
}
